import { DOCUMENT, CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { isDevMode, InjectionToken, SecurityContext, Injectable, Optional, Inject, Directive, Input, NgModule } from '@angular/core';
import { generate } from '@ant-design/colors';
import * as i1 from '@angular/common/http';
import { HttpClient } from '@angular/common/http';
import { Subject, of, Observable } from 'rxjs';
import { map, tap, finalize, catchError, share, filter, take } from 'rxjs/operators';
import * as i2 from '@angular/platform-browser';

const ANT_ICON_ANGULAR_CONSOLE_PREFIX = '[@ant-design/icons-angular]:';
function error(message) {
    console.error(`${ANT_ICON_ANGULAR_CONSOLE_PREFIX} ${message}.`);
}
function warn(message) {
    if (isDevMode()) {
        console.warn(`${ANT_ICON_ANGULAR_CONSOLE_PREFIX} ${message}.`);
    }
}
function getSecondaryColor(primaryColor) {
    return generate(primaryColor)[0];
}
function withSuffix(name, theme) {
    switch (theme) {
        case 'fill': return `${name}-fill`;
        case 'outline': return `${name}-o`;
        case 'twotone': return `${name}-twotone`;
        case undefined: return name;
        default: throw new Error(`${ANT_ICON_ANGULAR_CONSOLE_PREFIX}Theme "${theme}" is not a recognized theme!`);
    }
}
function withSuffixAndColor(name, theme, pri, sec) {
    return `${withSuffix(name, theme)}-${pri}-${sec}`;
}
function mapAbbrToTheme(abbr) {
    return abbr === 'o' ? 'outline' : abbr;
}
function alreadyHasAThemeSuffix(name) {
    return name.endsWith('-fill') || name.endsWith('-o') || name.endsWith('-twotone');
}
function isIconDefinition(target) {
    return (typeof target === 'object' &&
        typeof target.name === 'string' &&
        (typeof target.theme === 'string' || target.theme === undefined) &&
        typeof target.icon === 'string');
}
/**
 * Get an `IconDefinition` object from abbreviation type, like `account-book-fill`.
 * @param str
 */
function getIconDefinitionFromAbbr(str) {
    const arr = str.split('-');
    const theme = mapAbbrToTheme(arr.splice(arr.length - 1, 1)[0]);
    const name = arr.join('-');
    return {
        name,
        theme,
        icon: ''
    };
}
function cloneSVG(svg) {
    return svg.cloneNode(true);
}
/**
 * Parse inline SVG string and replace colors with placeholders. For twotone icons only.
 */
function replaceFillColor(raw) {
    return raw
        .replace(/['"]#333['"]/g, '"primaryColor"')
        .replace(/['"]#E6E6E6['"]/g, '"secondaryColor"')
        .replace(/['"]#D9D9D9['"]/g, '"secondaryColor"')
        .replace(/['"]#D8D8D8['"]/g, '"secondaryColor"');
}
/**
 * Split a name with namespace in it into a tuple like [ name, namespace ].
 */
function getNameAndNamespace(type) {
    const split = type.split(':');
    switch (split.length) {
        case 1: return [type, ''];
        case 2: return [split[1], split[0]];
        default: throw new Error(`${ANT_ICON_ANGULAR_CONSOLE_PREFIX}The icon type ${type} is not valid!`);
    }
}
function hasNamespace(type) {
    return getNameAndNamespace(type)[1] !== '';
}

function NameSpaceIsNotSpecifyError() {
    return new Error(`${ANT_ICON_ANGULAR_CONSOLE_PREFIX}Type should have a namespace. Try "namespace:${name}".`);
}
function IconNotFoundError(icon) {
    return new Error(`${ANT_ICON_ANGULAR_CONSOLE_PREFIX}the icon ${icon} does not exist or is not registered.`);
}
function HttpModuleNotImport() {
    error(`you need to import "HttpClientModule" to use dynamic importing.`);
    return null;
}
function UrlNotSafeError(url) {
    return new Error(`${ANT_ICON_ANGULAR_CONSOLE_PREFIX}The url "${url}" is unsafe.`);
}
function SVGTagNotFoundError() {
    return new Error(`${ANT_ICON_ANGULAR_CONSOLE_PREFIX}<svg> tag not found.`);
}
function DynamicLoadingTimeoutError() {
    return new Error(`${ANT_ICON_ANGULAR_CONSOLE_PREFIX}Importing timeout error.`);
}

const JSONP_HANDLER_NAME = '__ant_icon_load';
const ANT_ICONS = new InjectionToken('ant_icons');
class IconService {
    constructor(_rendererFactory, _handler, 
    // tslint:disable-next-line:no-any
    _document, sanitizer, _antIcons) {
        this._rendererFactory = _rendererFactory;
        this._handler = _handler;
        this._document = _document;
        this.sanitizer = sanitizer;
        this._antIcons = _antIcons;
        this.defaultTheme = 'outline';
        /**
         * All icon definitions would be registered here.
         */
        this._svgDefinitions = new Map();
        /**
         * Cache all rendered icons. Icons are identified by name, theme,
         * and for twotone icons, primary color and secondary color.
         */
        this._svgRenderedDefinitions = new Map();
        this._inProgressFetches = new Map();
        /**
         * Url prefix for fetching inline SVG by dynamic importing.
         */
        this._assetsUrlRoot = '';
        this._twoToneColorPalette = {
            primaryColor: '#333333',
            secondaryColor: '#E6E6E6'
        };
        /** A flag indicates whether jsonp loading is enabled. */
        this._enableJsonpLoading = false;
        this._jsonpIconLoad$ = new Subject();
        this._renderer = this._rendererFactory.createRenderer(null, null);
        if (this._handler) {
            this._http = new HttpClient(this._handler);
        }
        if (this._antIcons) {
            this.addIcon(...this._antIcons);
        }
    }
    set twoToneColor({ primaryColor, secondaryColor }) {
        this._twoToneColorPalette.primaryColor = primaryColor;
        this._twoToneColorPalette.secondaryColor =
            secondaryColor || getSecondaryColor(primaryColor);
    }
    get twoToneColor() {
        // Make a copy to avoid unexpected changes.
        return Object.assign({}, this._twoToneColorPalette);
    }
    /**
     * Disable dynamic loading (support static loading only).
     */
    get _disableDynamicLoading() {
        return false;
    }
    /**
     * Call this method to switch to jsonp like loading.
     */
    useJsonpLoading() {
        if (!this._enableJsonpLoading) {
            this._enableJsonpLoading = true;
            window[JSONP_HANDLER_NAME] = (icon) => {
                this._jsonpIconLoad$.next(icon);
            };
        }
        else {
            warn('You are already using jsonp loading.');
        }
    }
    /**
     * Change the prefix of the inline svg resources, so they could be deployed elsewhere, like CDN.
     * @param prefix
     */
    changeAssetsSource(prefix) {
        this._assetsUrlRoot = prefix.endsWith('/') ? prefix : prefix + '/';
    }
    /**
     * Add icons provided by ant design.
     * @param icons
     */
    addIcon(...icons) {
        icons.forEach(icon => {
            this._svgDefinitions.set(withSuffix(icon.name, icon.theme), icon);
        });
    }
    /**
     * Register an icon. Namespace is required.
     * @param type
     * @param literal
     */
    addIconLiteral(type, literal) {
        const [_, namespace] = getNameAndNamespace(type);
        if (!namespace) {
            throw NameSpaceIsNotSpecifyError();
        }
        this.addIcon({ name: type, icon: literal });
    }
    /**
     * Remove all cache.
     */
    clear() {
        this._svgDefinitions.clear();
        this._svgRenderedDefinitions.clear();
    }
    /**
     * Get a rendered `SVGElement`.
     * @param icon
     * @param twoToneColor
     */
    getRenderedContent(icon, twoToneColor) {
        // If `icon` is a `IconDefinition`, go to the next step. If not, try to fetch it from cache.
        const definition = isIconDefinition(icon)
            ? icon
            : this._svgDefinitions.get(icon) || null;
        if (!definition && this._disableDynamicLoading) {
            throw IconNotFoundError(icon);
        }
        // If `icon` is a `IconDefinition` of successfully fetch, wrap it in an `Observable`.
        // Otherwise try to fetch it from remote.
        const $iconDefinition = definition
            ? of(definition)
            : this._loadIconDynamically(icon);
        // If finally get an `IconDefinition`, render and return it. Otherwise throw an error.
        return $iconDefinition.pipe(map(i => {
            if (!i) {
                throw IconNotFoundError(icon);
            }
            return this._loadSVGFromCacheOrCreateNew(i, twoToneColor);
        }));
    }
    getCachedIcons() {
        return this._svgDefinitions;
    }
    /**
     * Get raw svg and assemble a `IconDefinition` object.
     * @param type
     */
    _loadIconDynamically(type) {
        // If developer doesn't provide HTTP module nor enable jsonp loading, just throw an error.
        if (!this._http && !this._enableJsonpLoading) {
            return of(HttpModuleNotImport());
        }
        // If multi directive ask for the same icon at the same time,
        // request should only be fired once.
        let inProgress = this._inProgressFetches.get(type);
        if (!inProgress) {
            const [name, namespace] = getNameAndNamespace(type);
            // If the string has a namespace within, create a simple `IconDefinition`.
            const icon = namespace
                ? { name: type, icon: '' }
                : getIconDefinitionFromAbbr(name);
            const suffix = this._enableJsonpLoading ? '.js' : '.svg';
            const url = (namespace
                ? `${this._assetsUrlRoot}assets/${namespace}/${name}`
                : `${this._assetsUrlRoot}assets/${icon.theme}/${icon.name}`) + suffix;
            const safeUrl = this.sanitizer.sanitize(SecurityContext.URL, url);
            if (!safeUrl) {
                throw UrlNotSafeError(url);
            }
            const source = !this._enableJsonpLoading
                ? this._http
                    .get(safeUrl, { responseType: 'text' })
                    .pipe(map(literal => (Object.assign(Object.assign({}, icon), { icon: literal }))))
                : this._loadIconDynamicallyWithJsonp(icon, safeUrl);
            inProgress = source.pipe(tap(definition => this.addIcon(definition)), finalize(() => this._inProgressFetches.delete(type)), catchError(() => of(null)), share());
            this._inProgressFetches.set(type, inProgress);
        }
        return inProgress;
    }
    _loadIconDynamicallyWithJsonp(icon, url) {
        return new Observable(subscriber => {
            const loader = this._document.createElement('script');
            const timer = setTimeout(() => {
                clean();
                subscriber.error(DynamicLoadingTimeoutError());
            }, 6000);
            loader.src = url;
            function clean() {
                loader.parentNode.removeChild(loader);
                clearTimeout(timer);
            }
            this._document.body.appendChild(loader);
            this._jsonpIconLoad$
                .pipe(filter(i => i.name === icon.name && i.theme === icon.theme), take(1))
                .subscribe(i => {
                subscriber.next(i);
                clean();
            });
        });
    }
    /**
     * Render a new `SVGElement` for a given `IconDefinition`, or make a copy from cache.
     * @param icon
     * @param twoToneColor
     */
    _loadSVGFromCacheOrCreateNew(icon, twoToneColor) {
        let svg;
        const pri = twoToneColor || this._twoToneColorPalette.primaryColor;
        const sec = getSecondaryColor(pri) || this._twoToneColorPalette.secondaryColor;
        const key = icon.theme === 'twotone'
            ? withSuffixAndColor(icon.name, icon.theme, pri, sec)
            : icon.theme === undefined
                ? icon.name
                : withSuffix(icon.name, icon.theme);
        // Try to make a copy from cache.
        const cached = this._svgRenderedDefinitions.get(key);
        if (cached) {
            svg = cached.icon;
        }
        else {
            svg = this._setSVGAttribute(this._colorizeSVGIcon(
            // Icons provided by ant design should be refined to remove preset colors.
            this._createSVGElementFromString(hasNamespace(icon.name) ? icon.icon : replaceFillColor(icon.icon)), icon.theme === 'twotone', pri, sec));
            // Cache it.
            this._svgRenderedDefinitions.set(key, Object.assign(Object.assign({}, icon), { icon: svg }));
        }
        return cloneSVG(svg);
    }
    _createSVGElementFromString(str) {
        const div = this._document.createElement('div');
        div.innerHTML = str;
        const svg = div.querySelector('svg');
        if (!svg) {
            throw SVGTagNotFoundError;
        }
        return svg;
    }
    _setSVGAttribute(svg) {
        this._renderer.setAttribute(svg, 'width', '1em');
        this._renderer.setAttribute(svg, 'height', '1em');
        return svg;
    }
    _colorizeSVGIcon(svg, twotone, pri, sec) {
        if (twotone) {
            const children = svg.childNodes;
            const length = children.length;
            for (let i = 0; i < length; i++) {
                const child = children[i];
                if (child.getAttribute('fill') === 'secondaryColor') {
                    this._renderer.setAttribute(child, 'fill', sec);
                }
                else {
                    this._renderer.setAttribute(child, 'fill', pri);
                }
            }
        }
        this._renderer.setAttribute(svg, 'fill', 'currentColor');
        return svg;
    }
}
IconService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.1.0", ngImport: i0, type: IconService, deps: [{ token: i0.RendererFactory2 }, { token: i1.HttpBackend, optional: true }, { token: DOCUMENT, optional: true }, { token: i2.DomSanitizer }, { token: ANT_ICONS, optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
IconService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.1.0", ngImport: i0, type: IconService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.1.0", ngImport: i0, type: IconService, decorators: [{
            type: Injectable
        }], ctorParameters: function () {
        return [{ type: i0.RendererFactory2 }, { type: i1.HttpBackend, decorators: [{
                        type: Optional
                    }] }, { type: undefined, decorators: [{
                        type: Optional
                    }, {
                        type: Inject,
                        args: [DOCUMENT]
                    }] }, { type: i2.DomSanitizer }, { type: undefined, decorators: [{
                        type: Optional
                    }, {
                        type: Inject,
                        args: [ANT_ICONS]
                    }] }];
    } });

function checkMeta(prev, after) {
    return prev.type === after.type && prev.theme === after.theme && prev.twoToneColor === after.twoToneColor;
}
class IconDirective {
    constructor(_iconService, _elementRef, _renderer) {
        this._iconService = _iconService;
        this._elementRef = _elementRef;
        this._renderer = _renderer;
    }
    ngOnChanges(changes) {
        if (changes.type || changes.theme || changes.twoToneColor) {
            this._changeIcon();
        }
    }
    /**
     * Render a new icon in the current element. Remove the icon when `type` is falsy.
     */
    _changeIcon() {
        return new Promise(resolve => {
            if (!this.type) {
                this._clearSVGElement();
                resolve(null);
                return;
            }
            const beforeMeta = this._getSelfRenderMeta();
            this._iconService.getRenderedContent(this._parseIconType(this.type, this.theme), this.twoToneColor).subscribe(svg => {
                // avoid race condition
                // see https://github.com/ant-design/ant-design-icons/issues/315
                const afterMeta = this._getSelfRenderMeta();
                if (checkMeta(beforeMeta, afterMeta)) {
                    this._setSVGElement(svg);
                    resolve(svg);
                }
                else {
                    resolve(null);
                }
            });
        });
    }
    _getSelfRenderMeta() {
        return {
            type: this.type,
            theme: this.theme,
            twoToneColor: this.twoToneColor
        };
    }
    /**
     * Parse a icon to the standard form, an `IconDefinition` or a string like 'account-book-fill` (with a theme suffixed).
     * If namespace is specified, ignore theme because it meaningless for users' icons.
     *
     * @param type
     * @param theme
     */
    _parseIconType(type, theme) {
        if (isIconDefinition(type)) {
            return type;
        }
        else {
            const [name, namespace] = getNameAndNamespace(type);
            if (namespace) {
                return type;
            }
            if (alreadyHasAThemeSuffix(name)) {
                if (!!theme) {
                    warn(`'type' ${name} already gets a theme inside so 'theme' ${theme} would be ignored`);
                }
                return name;
            }
            else {
                return withSuffix(name, theme || this._iconService.defaultTheme);
            }
        }
    }
    _setSVGElement(svg) {
        this._clearSVGElement();
        this._renderer.appendChild(this._elementRef.nativeElement, svg);
    }
    _clearSVGElement() {
        var _a;
        const el = this._elementRef.nativeElement;
        const children = el.childNodes;
        const length = children.length;
        for (let i = length - 1; i >= 0; i--) {
            const child = children[i];
            if (((_a = child.tagName) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === 'svg') {
                this._renderer.removeChild(el, child);
            }
        }
    }
}
IconDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.1.0", ngImport: i0, type: IconDirective, deps: [{ token: IconService }, { token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Directive });
IconDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.1.0", type: IconDirective, selector: "[antIcon]", inputs: { type: "type", theme: "theme", twoToneColor: "twoToneColor" }, usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.1.0", ngImport: i0, type: IconDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[antIcon]'
                }]
        }], ctorParameters: function () { return [{ type: IconService }, { type: i0.ElementRef }, { type: i0.Renderer2 }]; }, propDecorators: { type: [{
                type: Input
            }], theme: [{
                type: Input
            }], twoToneColor: [{
                type: Input
            }] } });

class IconModule {
}
IconModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.1.0", ngImport: i0, type: IconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
IconModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.1.0", ngImport: i0, type: IconModule, declarations: [IconDirective], imports: [CommonModule], exports: [IconDirective] });
IconModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.1.0", ngImport: i0, type: IconModule, providers: [IconService], imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.1.0", ngImport: i0, type: IconModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    exports: [IconDirective],
                    declarations: [IconDirective],
                    providers: [IconService]
                }]
        }] });

const manifest = {
    fill: [
        'account-book', 'alert', 'alipay-circle', 'aliwangwang', 'android', 'amazon-square', 'alipay-square', 'api', 'apple', 'appstore', 'amazon-circle', 'audio', 'backward', 'bank', 'behance-square', 'behance-circle', 'book', 'bell', 'bug', 'build', 'bulb', 'box-plot', 'calendar', 'calculator', 'car', 'caret-left', 'check-circle', 'check-square', 'caret-right', 'carry-out', 'ci-circle', 'chrome', 'camera', 'clock-circle', 'close-square', 'cloud', 'code', 'caret-up', 'codepen-circle', 'code-sandbox-square', 'code-sandbox-circle', 'caret-down', 'compass', 'contacts', 'codepen-square', 'close-circle', 'container', 'copyright-circle', 'copy', 'credit-card', 'crown', 'customer-service', 'control', 'dashboard', 'delete', 'dingtalk-circle', 'dislike', 'dingtalk-square', 'dollar-circle', 'database', 'diff', 'down-circle', 'down-square', 'dribbble-circle', 'dropbox-square', 'dribbble-square', 'edit', 'dropbox-circle', 'environment', 'euro-circle', 'experiment', 'exclamation-circle', 'eye-invisible', 'fast-backward', 'fast-forward', 'facebook', 'eye', 'file-add', 'file-image', 'file-markdown', 'file-excel', 'file', 'file-exclamation', 'file-ppt', 'file-unknown', 'file-text', 'file-word', 'file-pdf', 'file-zip', 'filter', 'folder-add', 'flag', 'folder', 'folder-open', 'fire', 'format-painter', 'frown', 'fund', 'gift', 'github', 'funnel-plot', 'golden', 'gold', 'google-plus-square', 'gitlab', 'hdd', 'google-circle', 'highlight', 'google-plus-circle', 'heart', 'hourglass', 'google-square', 'ie-circle', 'html5', 'idcard', 'home', 'forward', 'info-circle', 'insurance', 'instagram', 'like', 'layout', 'linkedin', 'ie-square', 'left-square', 'lock', 'interaction', 'meh', 'medicine-box', 'medium-circle', 'mac-command', 'left-circle', 'mail', 'minus-circle', 'medium-square', 'money-collect', 'mobile', 'message', 'minus-square', 'pay-circle', 'pause-circle', 'notification', 'phone', 'play-circle', 'picture', 'pie-chart', 'play-square', 'pound-circle', 'printer', 'plus-square', 'plus-circle', 'pushpin', 'property-safety', 'qq-square', 'qq-circle', 'project', 'reconciliation', 'question-circle', 'profile', 'reddit-square', 'red-envelope', 'rest', 'robot', 'right-circle', 'right-square', 'read', 'save', 'reddit-circle', 'rocket', 'security-scan', 'safety-certificate', 'shopping', 'setting', 'shop', 'sketch-square', 'skype', 'schedule', 'sliders', 'slack-circle', 'skin', 'signal', 'sketch-circle', 'snippets', 'smile', 'slack-square', 'step-backward', 'star', 'step-forward', 'switcher', 'sound', 'tablet', 'taobao-circle', 'tag', 'thunderbolt', 'tool', 'tags', 'trademark-circle', 'taobao-square', 'twitter-square', 'unlock', 'trophy', 'twitter-circle', 'stop', 'up-square', 'up-circle', 'usb', 'wechat', 'wallet', 'weibo-circle', 'warning', 'video-camera', 'weibo-square', 'windows', 'yahoo', 'zhihu-square', 'youtube', 'yuque', 'zhihu-circle'
    ],
    outline: [
        'alert', 'alibaba', 'aliyun', 'aim', 'amazon', 'ant-design', 'align-left', 'android', 'alipay', 'appstore-add', 'appstore', 'apple', 'area-chart', 'arrow-down', 'arrow-right', 'ant-cloud', 'arrow-left', 'audio-muted', 'audio', 'audit', 'arrow-up', 'backward', 'bar-chart', 'barcode', 'arrows-alt', 'bank', 'behance', 'bars', 'api', 'block', 'bg-colors', 'bold', 'border-left', 'border-bottom', 'border-outer', 'border', 'border-top', 'border-horizontal', 'book', 'behance-square', 'borderless-table', 'border-verticle', 'border-right', 'box-plot', 'bell', 'build', 'branches', 'bug', 'calculator', 'calendar', 'bulb', 'camera', 'aliwangwang', 'apartment', 'car', 'border-inner', 'caret-right', 'carry-out', 'caret-up', 'check-circle', 'caret-left', 'check', 'align-right', 'chrome', 'alipay-circle', 'caret-down', 'clear', 'clock-circle', 'ci-circle', 'close', 'ci', 'close-square', 'cloud-sync', 'cloud-download', 'check-square', 'cloud-upload', 'cloud', 'cluster', 'code', 'codepen', 'close-circle', 'coffee', 'codepen-circle', 'column-height', 'comment', 'compress', 'compass', 'cloud-server', 'container', 'copyright-circle', 'contacts', 'column-width', 'console-sql', 'copy', 'credit-card', 'crown', 'control', 'customer-service', 'code-sandbox', 'dash', 'dashboard', 'copyright', 'delete-row', 'delete-column', 'database', 'delivered-procedure', 'delete', 'dingding', 'diff', 'dislike', 'desktop', 'disconnect', 'down', 'double-right', 'deployment-unit', 'dot-chart', 'dollar-circle', 'double-left', 'drag', 'down-square', 'download', 'dingtalk', 'dropbox', 'ellipsis', 'dribbble-square', 'euro', 'down-circle', 'euro-circle', 'environment', 'enter', 'exception', 'exclamation', 'edit', 'expand', 'export', 'experiment', 'eye-invisible', 'exclamation-circle', 'eye', 'fast-forward', 'fall', 'expand-alt', 'fast-backward', 'field-time', 'dribbble', 'facebook', 'file-add', 'field-number', 'file-excel', 'file-gif', 'field-binary', 'file-image', 'file-markdown', 'dollar', 'file-jpg', 'file-ppt', 'file-done', 'file-pdf', 'file-protect', 'file-search', 'file-unknown', 'file-word', 'file-sync', 'file-text', 'filter', 'file-exclamation', 'field-string', 'file', 'file-zip', 'fire', 'flag', 'folder-view', 'folder', 'form', 'folder-add', 'fork', 'folder-open', 'frown', 'font-colors', 'fullscreen-exit', 'format-painter', 'fullscreen', 'font-size', 'fund', 'funnel-plot', 'gateway', 'gift', 'github', 'fund-view', 'fund-projection-screen', 'gitlab', 'gold', 'global', 'align-center', 'google-plus', 'group', 'hdd', 'heart', 'google', 'forward', 'heat-map', 'home', 'idcard', 'history', 'highlight', 'html5', 'ie', 'holder', 'info-circle', 'import', 'insert-row-above', 'hourglass', 'inbox', 'insert-row-below', 'insert-row-right', 'info', 'insert-row-left', 'function', 'insurance', 'laptop', 'interaction', 'issues-close', 'key', 'left-circle', 'instagram', 'layout', 'left-square', 'left', 'italic', 'line', 'loading-3-quarters', 'line-height', 'line-chart', 'linkedin', 'logout', 'loading', 'like', 'lock', 'mac-command', 'login', 'mail', 'link', 'medicine-box', 'man', 'meh', 'menu-unfold', 'medium-workmark', 'menu', 'menu-fold', 'minus-circle', 'minus', 'minus-square', 'monitor', 'node-collapse', 'message', 'node-index', 'more', 'merge-cells', 'notification', 'mobile', 'medium', 'one-to-one', 'partition', 'node-expand', 'number', 'money-collect', 'ordered-list', 'phone', 'pic-right', 'pause', 'paper-clip', 'pic-left', 'pie-chart', 'percentage', 'pay-circle', 'pic-center', 'play-circle', 'plus-circle', 'plus-square', 'plus', 'pound', 'play-square', 'picture', 'printer', 'project', 'profile', 'pound-circle', 'property-safety', 'question-circle', 'pushpin', 'radius-bottomleft', 'radar-chart', 'pull-request', 'pause-circle', 'radius-bottomright', 'qrcode', 'radius-setting', 'red-envelope', 'radius-upright', 'radius-upleft', 'reconciliation', 'reload', 'retweet', 'question', 'poweroff', 'read', 'right-circle', 'rest', 'right-square', 'right', 'rocket', 'rise', 'robot', 'rollback', 'qq', 'save', 'safety-certificate', 'rotate-right', 'search', 'send', 'rotate-left', 'share-alt', 'scissor', 'safety', 'schedule', 'reddit', 'shop', 'scan', 'setting', 'shopping-cart', 'shopping', 'security-scan', 'sisternode', 'select', 'shake', 'skype', 'shrink', 'slack-square', 'gif', 'smile', 'skin', 'sort-ascending', 'slack', 'solution', 'small-dash', 'star', 'step-backward', 'snippets', 'sliders', 'sound', 'stock', 'stop', 'step-forward', 'swap-left', 'subnode', 'swap-right', 'sync', 'tablet', 'switcher', 'account-book', 'sort-descending', 'split-cells', 'redo', 'table', 'team', 'tags', 'taobao-circle', 'swap', 'trademark', 'tool', 'tag', 'taobao', 'to-top', 'trophy', 'translation', 'ungroup', 'underline', 'twitter', 'sketch', 'unordered-list', 'thunderbolt', 'trademark-circle', 'up-square', 'strikethrough', 'upload', 'transaction', 'up', 'up-circle', 'usb', 'undo', 'usergroup-add', 'user', 'user-add', 'vertical-align-middle', 'usergroup-delete', 'user-delete', 'vertical-right', 'video-camera', 'vertical-left', 'user-switch', 'wallet', 'unlock', 'vertical-align-top', 'warning', 'video-camera-add', 'weibo-circle', 'weibo', 'verified', 'wechat', 'weibo-square', 'vertical-align-bottom', 'yahoo', 'windows', 'whats-app', 'yuque', 'wifi', 'youtube', 'zhihu', 'zoom-out', 'zoom-in', 'woman'
    ],
    twotone: [
        'account-book', 'api', 'appstore', 'audio', 'bank', 'bell', 'book', 'bug', 'build', 'bulb', 'box-plot', 'calendar', 'camera', 'calculator', 'car', 'check-square', 'carry-out', 'ci-circle', 'ci', 'clock-circle', 'check-circle', 'close-square', 'close-circle', 'code', 'cloud', 'container', 'copyright-circle', 'copy', 'credit-card', 'contacts', 'copyright', 'compass', 'customer-service', 'dashboard', 'control', 'database', 'crown', 'dollar-circle', 'dislike', 'diff', 'delete', 'dollar', 'down-square', 'environment', 'euro-circle', 'exclamation-circle', 'down-circle', 'euro', 'eye-invisible', 'edit', 'experiment', 'eye', 'file-add', 'file-excel', 'file-exclamation', 'file-image', 'file-markdown', 'file-pdf', 'file-unknown', 'file-word', 'file-zip', 'filter', 'fire', 'file', 'flag', 'file-ppt', 'folder', 'folder-open', 'folder-add', 'file-text', 'frown', 'fund', 'funnel-plot', 'gift', 'gold', 'hdd', 'heart', 'highlight', 'hourglass', 'home', 'html5', 'idcard', 'info-circle', 'interaction', 'insurance', 'layout', 'like', 'left-circle', 'lock', 'left-square', 'mail', 'medicine-box', 'meh', 'minus-square', 'message', 'minus-circle', 'money-collect', 'mobile', 'notification', 'pause-circle', 'alert', 'play-circle', 'phone', 'picture', 'play-square', 'pie-chart', 'plus-circle', 'plus-square', 'pound-circle', 'printer', 'project', 'profile', 'property-safety', 'question-circle', 'pushpin', 'red-envelope', 'reconciliation', 'rest', 'right-square', 'rocket', 'right-circle', 'safety-certificate', 'save', 'schedule', 'setting', 'security-scan', 'shop', 'skin', 'shopping', 'smile', 'snippets', 'sliders', 'star', 'sound', 'stop', 'tablet', 'tags', 'tag', 'switcher', 'tool', 'trophy', 'trademark-circle', 'thunderbolt', 'unlock', 'up-square', 'wallet', 'video-camera', 'warning', 'up-circle', 'usb'
    ]
};

/**
 * Generated bundle index. Do not edit.
 */

export { ANT_ICONS, ANT_ICON_ANGULAR_CONSOLE_PREFIX, DynamicLoadingTimeoutError, HttpModuleNotImport, IconDirective, IconModule, IconNotFoundError, IconService, NameSpaceIsNotSpecifyError, SVGTagNotFoundError, UrlNotSafeError, alreadyHasAThemeSuffix, cloneSVG, error, getIconDefinitionFromAbbr, getNameAndNamespace, getSecondaryColor, hasNamespace, isIconDefinition, manifest, mapAbbrToTheme, replaceFillColor, warn, withSuffix, withSuffixAndColor };
